import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { toast } from "sonner";
import { Megaphone, FileText, Upload, Trash2, Download, Sheet, Plus, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  { key: "announce", label: "Announcements", icon: Megaphone },
  { key: "docs",     label: "Documents",     icon: FileText },
  { key: "bulk",     label: "Bulk Products", icon: Sheet },
  { key: "refs",     label: "Plan · Packages", icon: LinkIcon },
];

export default function AdminDocs() {
  const [tab, setTab] = useState("announce");
  return (
    <DashboardLayout title="Documentation & Uploads" subtitle="Announcements · Documents · Bulk products · Plan reference">
      <div className="flex flex-wrap gap-2 border-b border-line mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`docs-tab-${t.key}`}
            className={`px-4 py-2 -mb-px border-b-2 text-sm inline-flex items-center gap-2 ${
              tab === t.key ? "border-royal text-royal font-medium" : "border-transparent text-navy/60 hover:text-royal"
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "announce" && <AnnouncePanel />}
      {tab === "docs"     && <DocsPanel />}
      {tab === "bulk"     && <BulkPanel />}
      {tab === "refs"     && <RefsPanel />}
    </DashboardLayout>
  );
}

/* ---- Announcements ---- */
function AnnouncePanel() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const load = () => api.get("/announcements").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!text.trim()) return;
    await api.post("/admin/announcements", { text });
    setText(""); toast.success("Announcement added"); load();
  };
  const del = async (id) => { await api.delete(`/admin/announcements/${id}`); load(); };
  return (
    <div className="bg-white border border-line rounded-sm p-6">
      <h3 className="font-serif text-lg text-royal mb-4">Marquee Announcements</h3>
      <div className="flex gap-3 mb-6">
        <input data-testid="ann-text" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="e.g. Grand Achievers Meet on 15 Feb 2026 — register now"
          className="flex-1 border border-line px-3 py-2.5 text-sm rounded-sm focus:border-gold focus:outline-none" />
        <button data-testid="ann-add" onClick={add} className="bg-royal text-white px-5 py-2.5 rounded-sm hover:bg-royal-deep inline-flex items-center gap-2 text-sm">
          <Plus size={14} /> Add
        </button>
      </div>
      <ul className="space-y-2">
        {items.length === 0 && <li className="text-navy/50 text-sm py-6 text-center">No active announcements.</li>}
        {items.map((a) => (
          <li key={a.id} data-testid={`ann-item-${a.id}`} className="flex items-center gap-3 p-3 border border-line rounded-sm">
            <Megaphone size={14} className="text-gold-deep" />
            <span className="flex-1 text-sm text-navy/80">{a.text}</span>
            <button onClick={() => del(a.id)} className="text-rose hover:text-royal p-1" title="Deactivate"><Trash2 size={14} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---- Documents ---- */
function DocsPanel() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);
  const load = () => api.get("/admin/docs").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const upload = async (f) => {
    if (!f || !title.trim()) { toast.error("Title + file required"); return; }
    const fd = new FormData(); fd.append("title", title); fd.append("file", f);
    try { await api.post("/admin/docs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); setTitle(""); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const download = (id) => {
    const t = localStorage.getItem("token") || "";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/docs/${id}/download`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = ""; a.click(); URL.revokeObjectURL(url);
      });
  };
  const del = async (id) => { await api.delete(`/admin/docs/${id}`); load(); };
  return (
    <div className="bg-white border border-line rounded-sm p-6">
      <h3 className="font-serif text-lg text-royal mb-4">Document Upload / Download Center</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6 items-end">
        <input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Document title (e.g. Feb Payout Circular)"
          className="md:col-span-2 border border-line px-3 py-2.5 text-sm rounded-sm focus:border-gold focus:outline-none" />
        <label className="border border-royal/40 text-royal px-4 py-2.5 rounded-sm hover:bg-royal/5 inline-flex items-center gap-2 text-sm cursor-pointer">
          <Upload size={14} /> Choose file & upload
          <input ref={fileRef} data-testid="doc-file" hidden type="file" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      </div>
      <ul className="space-y-2">
        {items.length === 0 && <li className="text-navy/50 text-sm py-6 text-center">No documents uploaded yet.</li>}
        {items.map((d) => (
          <li key={d.id} data-testid={`doc-item-${d.id}`} className="flex items-center gap-3 p-3 border border-line rounded-sm">
            <FileText size={16} className="text-royal" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-royal font-medium truncate">{d.title}</div>
              <div className="text-[11px] text-navy/50">{d.filename} · {(d.size / 1024).toFixed(0)} KB · {d.created_at?.slice(0, 10)}</div>
            </div>
            <button onClick={() => download(d.id)} className="text-royal hover:text-gold p-1"><Download size={14} /></button>
            <button onClick={() => del(d.id)} className="text-rose hover:text-royal p-1"><Trash2 size={14} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---- Bulk Excel ---- */
function BulkPanel() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const upload = async (f) => {
    if (!f) return; setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/admin/products/bulk-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data); toast.success(`Added ${r.data.added} · Updated ${r.data.updated}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-line rounded-sm p-6">
      <h3 className="font-serif text-lg text-royal mb-4">Bulk Product Upload (Excel)</h3>
      <p className="text-sm text-navy/70 mb-5 leading-relaxed">
        Upload an <code className="bg-ivory px-1.5 rounded">.xlsx</code> with columns:
        <span className="font-mono text-royal">sku · name · category · mrp · dp · sp · image · description · stock</span>.
        Existing SKUs will be updated; new ones will be inserted. Missing <code>sku</code> auto-generates a <code>SW-EXC-*</code> code.
      </p>
      <label className="inline-flex items-center gap-2 bg-royal text-white px-6 py-3 rounded-sm hover:bg-royal-deep cursor-pointer text-sm">
        <Upload size={14} /> {busy ? "Processing…" : "Upload Excel (.xlsx)"}
        <input data-testid="bulk-file" hidden type="file" accept=".xlsx" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {result && (
        <div className="mt-6 p-4 bg-ivory border border-line rounded-sm text-sm">
          <div className="text-royal font-medium">✓ Added {result.added} · Updated {result.updated}</div>
          {result.errors?.length > 0 && (
            <ul className="mt-2 text-xs text-rose list-disc pl-5">{result.errors.slice(0, 10).map((e, i) => <li key={`${e}-${i}`}>{e}</li>)}</ul>
          )}
        </div>
      )}
      <div className="mt-6 text-xs text-navy/60">
        Also add/edit products one at a time from <Link to="/admin/products" className="text-royal underline hover:text-gold">Admin · Products</Link>.
      </div>
    </div>
  );
}

/* ---- References (Plan / Packages) ---- */
function RefsPanel() {
  const refs = [
    { title: "Business Plan (Direct Selling · SP Only)", to: "/plan", note: "14-rank golden ladder · Monthly Bonus 2:1 · Weekly Matching 1:1" },
    { title: "Starter Packages", to: "/packages", note: "20 SP · 50 SP · 100 SP kits — Health, Beauty, IT Education, Family" },
    { title: "Public Grievance Form (DSR 2021)", to: "/grievance", note: "30-day resolution SLA per Consumer Protection Rules" },
    { title: "Terms · Privacy · Refund",           to: "/legal/terms", note: "Legal pages served to the public" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {refs.map((r) => (
        <Link key={r.to} to={r.to} target="_blank" data-testid={`ref-${r.to.replace(/\W+/g,'-')}`} className="block bg-white border border-line rounded-sm p-5 hover:border-gold hover:shadow-md transition-all">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gold-deep">
            <LinkIcon size={11} /> Reference
          </div>
          <div className="mt-2 font-serif text-lg text-royal">{r.title}</div>
          <div className="mt-1 text-xs text-navy/60">{r.note}</div>
        </Link>
      ))}
    </div>
  );
}
